import PageHeader from '@/components/base/PageHeader';
import QuickFacts from './components/QuickFacts';
import SectionNav from './components/SectionNav';
import StrategySection from './components/StrategySection';
import ExitManagement from './components/ExitManagement';
import SessionTimeline from './components/SessionTimeline';
import SetupSteps from './components/SetupSteps';
import AfterLaunch from './components/AfterLaunch';
import FaqSection from './components/FaqSection';
import RiskNote from './components/RiskNote';
import useCanonical from '@/hooks/useCanonical';
import { SETUP_STEPS, HIW_FAQ } from '@/pages/how-it-works/data';

const CANONICAL_URL = '/how-it-works';
const PAGE_TITLE = 'How GoldWarden EA Works — XAUUSD Trading Logic & MT5 Setup';
const PAGE_DESCRIPTION =
  'See how the free GoldWarden XAUUSD EA trades gold: market-structure entries, momentum and volatility filters, trading sessions and the full MT5 setup steps.';

const breadcrumbSchema = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'Home', item: '/' },
    { '@type': 'ListItem', position: 2, name: 'How It Works', item: CANONICAL_URL },
  ],
};

const webPageSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${CANONICAL_URL}#webpage`,
  name: 'How GoldWarden trades XAUUSD for you',
  description: PAGE_DESCRIPTION,
  url: CANONICAL_URL,
  inLanguage: 'en',
  isPartOf: { '@id': '/#website' },
  publisher: { '@id': '/#organization' },
};

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'How to install GoldWarden EA on MetaTrader 5',
  description:
    'Install the free GoldWarden XAUUSD expert advisor on MetaTrader 5 in six steps, from requesting the files to letting it trade.',
  totalTime: 'PT5M',
  step: SETUP_STEPS.map((item, index) => ({
    '@type': 'HowToStep',
    position: index + 1,
    name: item.title,
    text: item.text,
  })),
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: HIW_FAQ.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

export default function HowItWorks() {
  useCanonical(CANONICAL_URL);

  return (
    <>
      <title>{PAGE_TITLE}</title>
      <meta name="description" content={PAGE_DESCRIPTION} />
      <meta
        name="keywords"
        content="XAUUSD EA strategy, MT5 expert advisor setup, gold trading logic, free EA installation"
      />

      <meta property="og:type" content="article" />
      <meta property="og:site_name" content="GoldWarden EA" />
      <meta property="og:title" content={PAGE_TITLE} />
      <meta property="og:description" content={PAGE_DESCRIPTION} />
      <meta property="og:url" content={CANONICAL_URL} />
      <meta
        property="og:image"
        content="https://readdy.ai/api/search-image?query=Abstract%20dark%20financial%20banner%20with%20a%20glowing%20golden%20shield%20emblem%20centered%2C%20faint%20golden%20candlestick%20chart%20lines%20in%20the%20background%2C%20deep%20charcoal%20green%20to%20black%20gradient%2C%20luxurious%20molten%20gold%20accents%2C%20cinematic%20depth%20of%20field%2C%20minimal%20futuristic%20fintech%20aesthetic%2C%20no%20text%2C%20wide%20editorial%20composition&width=1200&height=630&seq=og-goldwarden-01&orientation=landscape&nocache=true"
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image:alt"
        content="GoldWarden XAUUSD EA trading logic and MetaTrader 5 setup walkthrough"
      />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={PAGE_TITLE} />
      <meta name="twitter:description" content={PAGE_DESCRIPTION} />
      <meta
        name="twitter:image"
        content="https://readdy.ai/api/search-image?query=Abstract%20dark%20financial%20banner%20with%20a%20glowing%20golden%20shield%20emblem%20centered%2C%20faint%20golden%20candlestick%20chart%20lines%20in%20the%20background%2C%20deep%20charcoal%20green%20to%20black%20gradient%2C%20luxurious%20molten%20gold%20accents%2C%20cinematic%20depth%20of%20field%2C%20minimal%20futuristic%20fintech%20aesthetic%2C%20no%20text%2C%20wide%20editorial%20composition&width=1200&height=630&seq=og-goldwarden-01&orientation=landscape&nocache=true"
      />
      <meta
        name="twitter:image:alt"
        content="GoldWarden XAUUSD EA trading logic and MetaTrader 5 setup walkthrough"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify([breadcrumbSchema, webPageSchema, howToSchema, faqSchema]) }}
      />

      <PageHeader
        breadcrumb="How It Works"
        eyebrow="The engine"
        title="How GoldWarden trades XAUUSD for you"
        subtitle="A transparent walkthrough of the logic, the trading sessions and the exact steps to get it running on your own MetaTrader 5 account."
      />

      <QuickFacts />
      <SectionNav />
      <StrategySection />
      <ExitManagement />
      <SessionTimeline />
      <AfterLaunch />
      <FaqSection />
      <SetupSteps />
      <RiskNote />
    </>
  );
}